import type { ComponentPropsWithoutRef, FunctionComponent } from "react";
import { cn } from "./cn";

/** Text color role — replaces antd Typography's `type` prop. */
export type TextTone = "default" | "muted" | "success" | "warning" | "danger";

const TONE_CLASS: Record<TextTone, string> = {
  default: "text-noa",
  muted: "text-noa-muted",
  success: "text-noa-success",
  warning: "text-noa-warning",
  danger: "text-noa-danger",
};

interface TypographyStyleProps {
  tone?: TextTone;
  /** Semibold weight (antd `strong`). */
  strong?: boolean;
  /** Single-line truncation with an ellipsis (antd `ellipsis`). */
  ellipsis?: boolean;
}

const typographyClass = ({ tone = "default", strong, ellipsis }: TypographyStyleProps) =>
  cn(
    TONE_CLASS[tone],
    "break-words",
    strong && "font-semibold",
    ellipsis && "inline-block max-w-full truncate align-bottom"
  );

export interface TextProps extends TypographyStyleProps, ComponentPropsWithoutRef<"span"> {
  /** Inline code look (antd `code`). */
  code?: boolean;
}

/** Inline text. Inherits font size; color from `tone`. */
export const Text: FunctionComponent<TextProps> = ({ tone, strong, ellipsis, code, className, ...props }) => {
  const classes = cn(typographyClass({ tone, strong, ellipsis }), className);

  if (code) {
    return (
      <code
        className={cn(
          "mx-[0.2em] rounded-[3px] border border-noa bg-noa-hover px-[0.4em] pt-[0.2em] pb-[0.1em] font-mono text-[85%]",
          classes
        )}
        {...props}
      />
    );
  }

  return <span className={classes} {...props} />;
};

export interface ParagraphProps extends TypographyStyleProps, ComponentPropsWithoutRef<"p"> {}

/** Block text with antd's 1em bottom margin. */
export const Paragraph: FunctionComponent<ParagraphProps> = ({ tone, strong, ellipsis, className, ...props }) => (
  <p className={cn("mt-0 mb-[1em]", typographyClass({ tone, strong, ellipsis }), className)} {...props} />
);

export type TitleLevel = 1 | 2 | 3 | 4 | 5;

// Sizes/line-heights match antd's fontSizeHeading{n} / lineHeightHeading{n} defaults.
const LEVEL_CLASS: Record<TitleLevel, string> = {
  1: "text-[38px] leading-[1.21]",
  2: "text-[30px] leading-[1.27]",
  3: "text-2xl leading-[1.33]",
  4: "text-xl leading-[1.4]",
  5: "text-base leading-normal",
};

export interface TitleProps extends Omit<TypographyStyleProps, "strong">, ComponentPropsWithoutRef<"h1"> {
  level?: TitleLevel;
}

/** Heading `h1`–`h5`, semibold, with antd's 0.5em bottom margin. */
export const Title: FunctionComponent<TitleProps> = ({ level = 1, tone, ellipsis, className, ...props }) => {
  const Heading = `h${level}` as const;

  return (
    <Heading
      className={cn(
        "mt-0 mb-[0.5em] font-semibold",
        LEVEL_CLASS[level],
        typographyClass({ tone, ellipsis }),
        // Headings default to the strong ink; an explicit tone overrides it.
        !tone && "text-noa-strong",
        className
      )}
      {...props}
    />
  );
};
